import { RotateCcw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { parseResults } from '@/modules/hiring-manager/chat/chatResults';
import {
  ReplyResults,
  type ReplyResultsHandlers,
} from '@/modules/hiring-manager/chat/replyResults/ReplyResults';
import type { ChatMessageApi } from '@/modules/hiring-manager/chat/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Message,
  MessageContent,
} from '@/modules/ui/components/message/Message';
import { Response } from '@/modules/ui/components/response/Response';

import { PendingReply } from './PendingReply';
import { useRevealText } from './useRevealText';

interface ChatThreadProps {
  messages: ChatMessageApi[];
  /** Replies that arrived while the chat was open: they appear as if streamed. */
  freshReplyIds?: ReadonlySet<string>;
  /** Messages from this index on were sent or answered while the chat was open: they fade in. */
  enterFrom?: number;
  /** Sends a message again when its reply failed; offered on the newest reply only. */
  onRetry?: (text: string) => void;
  retryDisabled?: boolean;
  /** What a reply's results need: without them, replies show only their text. */
  results?: ReplyResultsHandlers;
}

/** A chat's messages: the user's on the right, Finlay's replies in Markdown with the results drawn under them. */
export function ChatThread({
  messages,
  freshReplyIds,
  enterFrom = messages.length,
  onRetry,
  retryDisabled,
  results,
}: ChatThreadProps) {
  return (
    <div className="flex flex-col gap-6">
      {messages.map((message, index) => (
        // Keyed by position: a sent message and its pending reply change IDs once finlay-api saves them, and a new
        // key would fade them in again and restart the pending reply's phrases.
        <div
          className={cn(
            index >= enterFrom &&
              'ease-out-strong transition-[opacity,translate] duration-200 starting:translate-y-1 starting:opacity-0 motion-reduce:starting:translate-y-0',
          )}
          key={index}
        >
          <ChatMessage
            freshReplyIds={freshReplyIds}
            index={index}
            message={message}
            messages={messages}
            onRetry={onRetry}
            results={results}
            retryDisabled={retryDisabled}
          />
        </div>
      ))}
    </div>
  );
}

function ChatMessage({
  message,
  messages,
  index,
  freshReplyIds,
  onRetry,
  retryDisabled,
  results,
}: Omit<ChatThreadProps, 'enterFrom'> & {
  message: ChatMessageApi;
  index: number;
}) {
  if (message.role === 'User') return <UserMessage text={message.content} />;
  if (message.status === 'Pending') return <PendingReply />;
  if (message.status === 'Failed') {
    const asked = messages[index - 1];
    const canRetry = index === messages.length - 1 && asked?.role === 'User';
    return (
      <FailedReply
        disabled={retryDisabled}
        onRetry={canRetry && onRetry ? () => onRetry(asked.content) : undefined}
      />
    );
  }
  const reveal = freshReplyIds?.has(message.id) ?? false;
  return (
    <div className="flex flex-col gap-3">
      <Reply
        // A reply that replaces a pending one at the same place starts its reveal afresh.
        key={message.id}
        reveal={reveal}
        text={message.content}
      />
      {results && (
        <ReplyResults results={parseResults(message.results)} {...results} />
      )}
    </div>
  );
}

function UserMessage({ text }: { text: string }) {
  return (
    <Message className="py-0" from="user">
      {/* Shown as typed: a pasted job description shouldn't turn into Markdown headings. */}
      <MessageContent className="group-[.is-user]:bg-muted group-[.is-user]:text-foreground rounded-2xl px-4 py-2.5 whitespace-pre-wrap shadow-none group-[.is-user]:border-0">
        {text}
      </MessageContent>
    </Message>
  );
}

function Reply({ text, reveal }: { text: string; reveal: boolean }) {
  const shown = useRevealText(text, reveal);

  return (
    <Message className="py-0 [&>div]:max-w-full" from="assistant">
      <MessageContent className="group-[.is-assistant]:text-foreground p-0 shadow-none group-[.is-assistant]:bg-transparent">
        <Response>{shown}</Response>
      </MessageContent>
    </Message>
  );
}

function FailedReply({
  disabled,
  onRetry,
}: {
  disabled?: boolean;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <span className="text-muted-foreground">
        I couldn&apos;t answer this one.
      </span>
      {onRetry && (
        <Button
          disabled={disabled}
          onClick={onRetry}
          size="sm"
          variant="outline"
        >
          <RotateCcw className="size-3.5" />
          Retry
        </Button>
      )}
    </div>
  );
}
